import { useCallback, useEffect, useMemo, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { api, setUnauthorizedHandler } from "@/lib/api";
import { ThemeProvider, useTheme } from "@/lib/theme";
import { ScopeProvider } from "@/lib/scope";
import { AppContext, useApp } from "@/lib/app";
import { useFeedStatus, useScopes } from "@/lib/queries";
import { fmtDateLong } from "@/lib/format";
import { Sidebar } from "@/components/shell/Sidebar";
import { ScopeBar } from "@/components/shell/ScopeBar";
import { LoadingScreen } from "@/components/dashboard/LoadingScreen";
import Login from "@/pages/Login";
import ExecutiveSummary from "@/pages/ExecutiveSummary";
import CountAnalytics from "@/pages/CountAnalytics";
import SalesPos from "@/pages/SalesPos";
import AiGrowth from "@/pages/AiGrowth";
import Forecast from "@/pages/Forecast";
import CalendarEvents from "@/pages/CalendarEvents";
import UsersAdmin from "@/pages/UsersAdmin";
import LiveFeed from "@/pages/LiveFeed";

/* per-route scope bar behaviour */
const PAGE_META = {
  "/": { lockScope: true },
  "/count": {},
  "/sales": {},
  "/ai-growth": {},
  "/forecast": { hidePeriod: true },
  "/calendar": {},
  "/admin/users": { showScope: false, title: "Users & access" },
  "/admin/live": { showScope: false, title: "Live Count Feed · integration" },
};

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo({ top: 0, behavior: "auto" });
  }, [pathname, hash]);
  return null;
};

const RequireAdmin = ({ children }) => {
  const { isAdmin } = useApp();
  useEffect(() => {
    if (!isAdmin) toast.error("That page is for administrators only");
  }, [isAdmin]);
  return isAdmin ? children : <Navigate to="/" replace />;
};

function Shell({ user, onLogout }) {
  const isAdmin = user?.role === "admin";
  const scopesQ = useScopes();
  const feedQ = useFeedStatus();
  const location = useLocation();
  const { theme } = useTheme();
  const meta = PAGE_META[location.pathname] || {};
  const feed = feedQ.data || null;
  const ctx = useMemo(
    () => ({ user, isAdmin, logout: onLogout, feed, scopes: scopesQ.data?.scopes || [], clusters: scopesQ.data?.clusters || [], anchor: scopesQ.data?.anchor || null, periods: scopesQ.data?.periods || [] }),
    [user, isAdmin, onLogout, feed, scopesQ.data]
  );

  return (
    <AppContext.Provider value={ctx}>
      <TooltipProvider delayDuration={150}>
        <div className="noise min-h-screen">
          <Sidebar
            isAdmin={isAdmin}
            feed={feed}
            footer={
              <>
                <div data-testid="sidebar-anchor">{ctx.anchor ? `Data to ${fmtDateLong(ctx.anchor)}` : "Loading data registry…"}</div>
                <div>Nightly AI refresh 06:00 MYT · {isAdmin ? "Administrator" : "Viewer"}</div>
              </>
            }
          />
          <div className="lg:pl-[272px] relative z-10 flex flex-col min-h-screen">
            <ScopeBar user={user} onLogout={onLogout} scopes={ctx.scopes} clusters={ctx.clusters} anchor={ctx.anchor} feed={feed} isAdmin={isAdmin} showScope={meta.showScope !== false} lockScope={!!meta.lockScope} hidePeriod={!!meta.hidePeriod} title={meta.title} />
            <main className="px-4 sm:px-6 lg:px-8 py-6 md:py-8 max-w-[1400px] w-full flex-1" data-testid="main-content">
              <ScrollToTop />
              <Routes>
                <Route path="/" element={<ExecutiveSummary />} />
                <Route path="/count" element={<CountAnalytics />} />
                <Route path="/sales" element={<SalesPos />} />
                <Route path="/ai-growth" element={<AiGrowth />} />
                <Route path="/forecast" element={<Forecast />} />
                <Route path="/calendar" element={<CalendarEvents />} />
                <Route path="/admin/users" element={<RequireAdmin><UsersAdmin /></RequireAdmin>} />
                <Route path="/admin/live" element={<RequireAdmin><LiveFeed /></RequireAdmin>} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </main>
          </div>
          <Toaster theme={theme} position="bottom-right" closeButton />
        </div>
      </TooltipProvider>
    </AppContext.Provider>
  );
}

function AuthGate() {
  const [authState, setAuthState] = useState({ status: "checking", user: null }); // checking | anonymous | authenticated
  const qc = useQueryClient();
  const { theme } = useTheme();

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setAuthState((s) => (s.status === "authenticated" ? { status: "anonymous", user: null } : s));
    });
    api
      .me()
      .then((res) => setAuthState({ status: "authenticated", user: res.user }))
      .catch(() => setAuthState({ status: "anonymous", user: null }));
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch (e) {
      console.error(e);
    }
    qc.clear();
    setAuthState({ status: "anonymous", user: null });
    toast.success("Signed out");
  }, [qc]);

  if (authState.status === "checking") return <LoadingScreen />;
  if (authState.status === "anonymous") {
    return (
      <TooltipProvider delayDuration={150}>
        <Login
          onLoggedIn={(user) => {
            qc.clear();
            setAuthState({ status: "authenticated", user });
          }}
        />
        <Toaster theme={theme} position="bottom-right" closeButton />
      </TooltipProvider>
    );
  }
  return (
    <ScopeProvider>
      <Shell key={authState.user?.id} user={authState.user} onLogout={logout} />
    </ScopeProvider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthGate />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
